import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, Signal } from '@angular/core';
import { WorkflowModel } from '@models/workflow.model';
import { Store } from '@ngrx/store';
import { WorkflowsSelectors } from '@states/workflow/workflow.selector-types';
import { createRunListTableConfig, WorkflowRunItemRow } from './run-list-table.util';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SharedModels } from '@share-models';
import { UiDatasetTableComponent, UiNoDataComponent, UiPreloaderComponent } from '@ui';
import { features } from '@consts/text.const';
import { WorkflowActions } from '@states/workflow/workflow.action.types';
import { Router } from '@angular/router';
import { routerSegments } from '@consts/routes';
import { MatDialog } from '@angular/material/dialog';
import { ViewStructureModalComponent } from 'src/app/shared/modals/view-structure-modal/view-structure-modal.component';

@Component({
  selector: 'run-list-table',
  imports: [
    TranslatePipe,
    UiDatasetTableComponent,
    UiNoDataComponent,
    UiPreloaderComponent,
  ],
  templateUrl: './run-list-table.component.html',
  styleUrl: './run-list-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RunListTableComponent implements OnInit {
  private readonly store$ = inject(Store);
  private readonly translateService = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);

  public graphId = input.required<string>();
  public orgId = input.required<string>();

  protected readonly noData = features.tags.noResults;
  protected readonly viewStates = SharedModels.ViewState;

  public selectIsRunListLoadingSig: Signal<boolean> =
    this.store$.selectSignal(WorkflowsSelectors.selectIsRunListLoading);
  public selectRunListSig: Signal<WorkflowModel.WorkflowRunSummaryItem[]> =
    this.store$.selectSignal(WorkflowsSelectors.selectRunList);

  public readonly tableConfig = createRunListTableConfig({ onRowClick: (row: WorkflowRunItemRow) => this.goToStepsPage(row) });

  public rowsSig: Signal<WorkflowRunItemRow[]> = computed(() => {
    const runList = this.selectRunListSig();

    return runList?.map((run) => {
      return {
        ...run,
        statusLabel: this.translateService.instant(WorkflowModel.RunStatusMap[run.status]),
      }
    })
  });

  public ngOnInit(): void {
    this.store$.dispatch(WorkflowActions.getWorkflowRunList({
      orgId: this.orgId(),
      graphId: this.graphId(),
    }))
  }

  protected readonly viewStateSig = computed(() => {
    if (this.rowsSig()?.length) {
      return SharedModels.ViewState.HAS_CONTENT;
    }

    return SharedModels.ViewState.INITIAL_EMPTY;
  })

  public onRowAction(event: { actionId: string; row: WorkflowRunItemRow }): void {
    switch (event.actionId) {
      case 'cancelRun':
        this.cancelRun(event.row.runId);
        break;
      case 'viewJsonOutput':
        this.viewJsonOutput(event.row.output);
        break;
    }
  }

  private cancelRun(runId: string): void {
    this.store$.dispatch(WorkflowActions.cancelRunWorkflow({
      orgId: this.orgId(),
      graphId: this.graphId(),
      runId: runId,
    }))
  }

  private viewJsonOutput(output: Record<string, unknown>): void {
    this.dialog
      .open(ViewStructureModalComponent, {
        panelClass: 'thumbnail-dialog-wrapper',
        width: '80vw',
        height: '600px',
        data: output,
      })
      .afterClosed();
  }

  private goToStepsPage(row: WorkflowRunItemRow) {
    this.router.navigate([routerSegments.workflows, this.graphId(), routerSegments.runs, row.runId, routerSegments.steps]);
  }
}
